import { useEffect, useRef, useState } from "react";
import AuthLayout from "../../../components/layouts/auth";
import TablePagination from "../../../components/table/table-pagination";
import { numberWithCommas, strReplace } from "../../../lib/helper-support";
import { useBusinessMerchant } from "../../../composables/business-merchant/useBusinessMerchant";
import TableSkeleton from "../../../components/loader/table-placeholder-loader";
import BusinessMerchantProps from "../../../types/business-merchant-type";
import { Link } from "react-router-dom";
import moment from "moment";
import { BusinessVerifiedStatusBadge } from "../../../components/ui/BusinessMerchant";
import SearchInput from "../../../components/form/search-input";
import { useDebounced } from "../../../hooks/useDebounce";
import { ChevronDown, Filter } from "lucide-react";
import Button from "../../../components/form/button";
import { useApi } from "../../../providers/ApiProvider";

const BusinessMerchant = () => {
  const api = useApi();
  const [page, setCurrentPage] = useState<number>(1);

  const [showFilters, setShowFilters] = useState<boolean>(false);

  const [search, setSearch] = useState<string>("");

  const [verificationStage, setVerificationStage] = useState<string>("all");

  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const searchDebounced = useDebounced({
    value: search,
  });

  const verificationStageDebounced = useDebounced({
    value: verificationStage,
  });

  const { data, isLoading } = useBusinessMerchant({
    page,
    search: searchDebounced,
    filter_verified:
      verificationStageDebounced === "all" ? "" : verificationStageDebounced,
  });

  const showFiltersRef = useRef<HTMLDivElement>(null);

  const onShowFilter = (triggerBy = "default") => {
    const filters = document.getElementById("filters");

    if (triggerBy === "outsideEvent") {
      filters?.classList.add(`-translate-y-5`);
      filters?.classList.add("opacity-0");
      filters?.classList.remove("z-20");
      setShowFilters(false);
      return;
    }

    filters?.classList.toggle(`-translate-y-5`);

    filters?.classList.toggle("opacity-0");

    filters?.classList.toggle("z-20");

    setShowFilters(!showFilters);
  };

  const resetFilter = () => {
    setVerificationStage("all");
    //onShowFilter();
  };

  const handleOnClickOutside = (event: MouseEvent) => {
    if (
      showFiltersRef.current &&
      !showFiltersRef.current.contains(event.target as Node)
    )
      onShowFilter("outsideEvent");
  };

  useEffect(() => {
    // Add event listener when the component mounts
    document.addEventListener("click", handleOnClickOutside);

    // Cleanup event listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", handleOnClickOutside);
    };
  });

  const ExportBusinessMerchants = async () => {
    setExportLoading(true);
    await api.DOWNLOAD({
      endpoint: `business-merchant/export`,
      params: {
        search: searchDebounced,
        filter_verified:
          verificationStageDebounced === "all"
            ? ""
            : verificationStageDebounced,
      },
      filename: `business-merchants-${moment().format("YYYY-MM-DD")}.csv`,
    });

    // await api.POST({
    //   endpoint: `business-merchant/export`,
    //   payload: {
    //     search: searchDebounced,
    //     filter_verified:
    //       verificationStageDebounced === "all"
    //         ? ""
    //         : verificationStageDebounced,
    //   },
    //   refreshKeys: [["business-merchant"]],
    // });

    setExportLoading(false);
  };

  return (
    <AuthLayout>
      <div className="items-center space-y-5 lg:justify-between lg:flex md:flex md:space-y-0">
        <div className="flex items-center gap-2 md:gap-6 flex-wrap">
          <h1 className="page-title">Business Merchants</h1>
          <span className="text-tertiary font-[600] text-[14px]">
            {numberWithCommas(data?.total ?? 0)} business merchants
          </span>
        </div>
        <Button
          onClick={() => ExportBusinessMerchants()}
          isLoading={exportLoading}
          text="Export data"
          className="px-8 text-white input w-fit shadow-sm flex items-center space-x-2"
        />
      </div>

      <div className="relative">
        <div className="items-center mt-5 lg:justify-between lg:flex">
          <SearchInput
            placeholder="Find merchant"
            value={search}
            onChangeInput={(e) => setSearch(e.target.value)}
          />
          <div ref={showFiltersRef}>
            <div className="text-[13px] flex items-center space-x-5 mt-5 lg:mt-0">
              <div
                onClick={() => onShowFilter()}
                className="z-10 flex items-center space-x-3 cursor-pointer"
              >
                <Filter size={13} />
                <p className="text-main-dark font-[700]">Filter by</p>
                <ChevronDown size={13} />
              </div>
              {/* <div className='flex items-center space-x-1 text-[#778CA2]'>
                                    <CalendarDays size={13} />
                                    <p className='font-[500] uppercase'>date</p>
                                </div> */}
              {/* <div onClick={() => onShowFilter()} className='z-10 flex items-center space-x-3 cursor-pointer'>
                                    <p className='text-[#252631] font-[500] uppercase'>
                                        {moment(createdFrom).format('MMMM YYYY')}
                                    </p>
                                    <ChevronDown size={13} />
                                </div> */}
            </div>
            {/* filter */}
            <div
              id="filters"
              className={`transition-all duration-500 ease-in-out transform w-full lg:w-[25em] absolute -translate-y-5 opacity-0
                                                    border border-[#135EE5] p-5 rounded-[15px] bg-white   lg:right-0 lg:top-10 top-24 h-[30em]`}
            >
              <div className="flex justify-between font-[500]">
                <span>Filters</span>
                <span
                  onClick={() => resetFilter()}
                  className="text-red-600 cursor-pointer"
                >
                  Reset
                </span>
              </div>
              <div className="grid h-[25em] mt-5 overflow-y-auto gap-y-6">
                <div>
                  <h4 className="text-[15px] mb-2">Verification Stage</h4>
                  <div className="border input">
                    <select
                      value={verificationStage}
                      onChange={(e) => {
                        setVerificationStage(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full text-gray-600 focus:outline-none"
                    >
                      {["all", "verified", "not_verified"].map(
                        (item, index) => (
                          <option key={index} value={item}>
                            {strReplace(item, "_", " ")}
                          </option>
                        ),
                      )}
                    </select>
                    <ChevronDown size={13} />
                  </div>
                </div>
              </div>
            </div>
            {/* --/ filter */}
          </div>
        </div>
        {/* table */}
        <div className="relative overflow-x-auto">
          <table className="mt-10 text-tertiary font-[600] text-[14px] w-full relative z-10 whitespace-nowrap">
            <thead>
              <tr className="border-b border-[#E6EAF0]">
                <td className="px-2 py-3">
                  {/* <input type='checkbox' /> */}#
                </td>
                <td className="px-6 py-3">Business Name</td>
                <td className="px-6 py-3">Ctag</td>
                <td className="px-6 py-3">Code</td>
                <td className="px-6 py-3">Email</td>
                <td className="px-6 py-3">Phone Number</td>
                <td className="px-6 py-3">Verification Stage</td>
                <td className="px-6 py-3">Account Created</td>
              </tr>
            </thead>
            <tbody className="relative">
              {isLoading && <TableSkeleton numberOfColumns={8} />}
              {!isLoading &&
                data?.items?.map(
                  (item: BusinessMerchantProps, index: number) => (
                    <tr
                      key={index}
                      className="border-b border-[#E6EAF0] relative"
                      // style={{ backgroundColor: selectedUserAccounts.includes(user.cTag) ? '#E6EAF0' : '' }}
                    >
                      <td className="px-2 py-3">
                        {index + 1}
                        <Link
                          to={`/business-merchants/${item.code}`}
                          className="absolute top-0 bottom-0 left-0 w-full h-full hover:bg-[#E6EAF0] opacity-[0.2]"
                        />
                      </td>
                      <td className="px-4 py-3">{item.business_name}</td>
                      <td className="px-4 py-3">{item.ctag}</td>
                      <td className="px-4 py-3">{item.code}</td>
                      <td className="px-4 py-3">{item.email}</td>
                      <td className="px-4 py-3">{item.phone_number || "-"}</td>
                      <td className="px-4 py-3">
                        <BusinessVerifiedStatusBadge
                          verified={item.is_verified}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {moment(item.created_at).format("MMM D, YYYY")}
                      </td>
                    </tr>
                  ),
                )}
            </tbody>
          </table>
        </div>
        {/*--/ table */}
        {/* pagination */}
        {!isLoading && (
          <div className="mt-5">
            <TablePagination
              totalPages={data?.totalPages ?? 0}
              onPageChange={(page) => setCurrentPage(page)}
              currentPage={data?.page}
            />
          </div>
        )}
        {/* --/ pagination */}
      </div>
    </AuthLayout>
  );
};

export default BusinessMerchant;
